import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Loader } from 'components/Shared/Loader'
import Table, { Order } from 'components/Shared/Table'
import { CalendarFilter } from './CalendarFilter'
import { deleteOrder, fetchOrders } from 'services/OrderService'
import { fetchDispatchers, fetchForwarders, subscribeOrderUpdated } from 'services'
import moment from 'moment'
import { momentToISOLocalDate } from 'utils/date'
import { useErrorHandling } from 'context/errorHandling'
import { useWebSocketConnection } from 'context'
import { isEqual, omit } from 'lodash'
import { createColumnDefinitions } from 'components/Order/columnDefinitions'
import {
  ORDER_COST_READ,
  ORDER_CREATE,
  ORDER_DELETE,
  ORDER_DISPATCHER_READ,
  ORDER_EDIT,
  ORDER_FORWARDER_READ,
  ORDER_INCOME_READ,
  useAuth
} from 'context/auth'
import { FinancialSummaryRow } from 'components/Order/OrderTable/FinancialSummaryRow/FinancialSummaryRow'
import { useUserGroups } from 'context/userGroups'
import { HTTP_406_ERROR } from "../../../Constants";
import { CANNOT_DELETE_INVOICED_TRANSACTION } from "../../../services/errorCodes";
import ApiError from "../../../constants/ApiError";

const fetchData = queryParams => {
  const { orderBy, filters, ...rest } = queryParams
  let newOrderBy = orderBy
  if (orderBy === 'orderStatuses') {
    newOrderBy = 'status'
  }
  const newFilters = filters.map(({ key, value }) => {
    if (key === 'creationDateGe' || key === 'creationDateLe') {
      return ({
        key: key,
        value: momentToISOLocalDate(moment(value))
      })
    }
    return ({ key, value })
  })
  const newQueryParams = {
    ...rest,
    orderBy: newOrderBy,
    filters: newFilters
  }
  return fetchOrders(newQueryParams)
}

const backgroundRefreshConfig = {
  shouldHighlightRow: (oldItem, newItem) => {
    const ignoredColumns = ['distanceToNextDeliveryPointKm']
    return !isEqual(
      omit(oldItem, ignoredColumns),
      omit(newItem, ignoredColumns)
    )
  }
}

const sortConfig = {
  defaultSortParameters: [{ param: 'status', order: Order.Ascending }],
  columnsGroupedBy: ['status']
}

export function OrdersTable () {
  const [forwarders, setForwarders] = useState()
  const [dispatchers, setDispatchers] = useState()
  const { t } = useTranslation()
  const history = useHistory()

  const { handleError } = useErrorHandling()

  const tableRef = useRef()

  const { hasAnyPrivileges } = useAuth()
  const { isInMainGroup, hasMainGroupById } = useUserGroups()

  const hasDispatcherReadPrivilege = hasAnyPrivileges([ORDER_DISPATCHER_READ])
  useEffect(() => {
    if (hasDispatcherReadPrivilege) {
      fetchDispatchers()
        .then(dispatchersResponse => {
          let dispatcherOptions = dispatchersResponse.map(
            dispatcher => ({
              label: dispatcher.firstName + ' ' + dispatcher.lastName,
              value: dispatcher.id
            }))
          setDispatchers(dispatcherOptions)
        })
        .catch(handleError)
    }
  }, [hasDispatcherReadPrivilege, handleError])

  const hasForwarderReadPrivilege = hasAnyPrivileges([ORDER_FORWARDER_READ])
  useEffect(() => {
    if (hasForwarderReadPrivilege) {
      fetchForwarders()
        .then(forwardersResponse => {
          let forwardersOptions = forwardersResponse.map(
            forwarder => ({
              label: forwarder.firstName + ' ' + forwarder.lastName,
              value: forwarder.id
            }))
          setForwarders(forwardersOptions)
        })
        .catch(handleError)
    }
  }, [hasForwarderReadPrivilege, handleError])

  const isWebSocketConnection = useWebSocketConnection()

  useEffect(() => {
    if (isWebSocketConnection) {
      return subscribeOrderUpdated(() => {
        tableRef.current.refreshContentInBackground()
      })
    }
  }, [isWebSocketConnection])

  const columnDefinitions = useMemo(() => {
    if ((forwarders || !hasForwarderReadPrivilege) &&
      (dispatchers || !hasDispatcherReadPrivilege)) {
      return createColumnDefinitions(hasAnyPrivileges, t, forwarders, dispatchers, tableRef)
    }
    return null
  }, [forwarders, dispatchers, hasAnyPrivileges, hasForwarderReadPrivilege, hasDispatcherReadPrivilege, t])

  const addOrder = () => {
    history.push('/orders/create')
  }

  const showDetails = (order) => {
    history.push(`/orders/details/${order.id}`)
  }

  const copyOrder = (order) => {
    history.push({
      pathname: '/orders/create',
      state: { idOfCopiedOrder: order.id }
    })
  }

  const editOrder = (order) => {
    history.push(`/orders/edit/${order.id}`)
  }

  const hasCreateAuthority = hasAnyPrivileges([ORDER_CREATE]) && isInMainGroup()
  const hasEditAuthority = (orderGroupId) => hasAnyPrivileges([ORDER_EDIT]) && hasMainGroupById(orderGroupId)
  const hasDeleteAuthority = (orderGroupId) => hasAnyPrivileges([ORDER_DELETE]) && hasMainGroupById(orderGroupId)

  function getAddButtonTooltip () {
    if (!hasAnyPrivileges([ORDER_CREATE])) return t('orderTable.addButton.tooltip.withoutOrderCreatePrivilege')
    else if (!isInMainGroup()) return t('orderTable.addButton.tooltip.withoutMainGroupDefined')
    return ''
  }

  const addButton = {
    label: 'orderTable.addButton.label',
    onClick: addOrder,
    isClickable: hasCreateAuthority,
    tooltip: getAddButtonTooltip()
  }

  const onDeleteError = (error) => {
    if (error.response?.data?.errorCode === CANNOT_DELETE_INVOICED_TRANSACTION) {
      handleError(error, {
        [HTTP_406_ERROR]: ApiError.CannotDeleteOrderWithInvoicedTransaction
      })
    } else {
      handleError(error, {
        [HTTP_406_ERROR]: ApiError.GenericError
      })
    }
  }

  const menuOptions = {
    edit: {
      onClick: editOrder,
      isEditable: (order) => hasEditAuthority(order.groupId)
    },
    copy: {
      onClick: copyOrder,
      isCopyable: () => hasCreateAuthority
    },
    delete: {
      onDelete: deleteOrder,
      onDeleteError: onDeleteError,
      deleteDialog: {
        title: 'orderTable.deleteDialog.title',
        content: 'orderTable.deleteDialog.content'
      },
      isDeletable: (order) => hasDeleteAuthority(order.groupId)
    }
  }

  return columnDefinitions
    ? (
      <Table
        title={'order.title'}
        addButton={addButton}
        fetchData={fetchData}
        columns={columnDefinitions}
        tableKey={'orders'}
        onRowClick={showDetails}
        menu={menuOptions}
        renderToolbarLeftPanel={() =>
          <CalendarFilter
            filters={tableRef.current?.filters || []}
            onFiltersChanged={tableRef.current?.setFilters || (() => {
            })}/>
        }
        renderBottomExtraRow={() =>
          hasAnyPrivileges([ORDER_INCOME_READ]) && hasAnyPrivileges([ORDER_COST_READ]) && <FinancialSummaryRow
            filters={tableRef.current?.filters}/>
        }
        sort={sortConfig}
        backgroundRefresh={backgroundRefreshConfig}
        ref={tableRef}
      />
    )
    : <Loader/>
}
